import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  BarChart3, PieChart as PieChartIcon, Users, Building2, CreditCard,
  FileText, Shield, MapPin, Globe2, Wifi, RefreshCw, ChevronDown,
  ChevronRight, X, ExternalLink, Search, TrendingUp, CheckCircle,
  AlertCircle, Clock, Lock, Timer, Landmark, Banknote, Layers, Download, Filter,
  Plus, Minus, Calendar as CalendarIcon, Play, RotateCcw, Loader2, Tag
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { leadAnalyticsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import GeoPropertyAnalytics from '../components/GeoPropertyAnalytics';
import OffboardedAnalytics from '../components/OffboardedAnalytics';
import AnalyticsFilterBar from '../components/AnalyticsFilterBar';
import TimeComparisonResults from '../components/TimeComparisonResults';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const EMPTY_FILTERS = {
  date_range: { start: '', end: '' },
  search: '',
  lead_stage: [], lead_owner: [], source: [], property_status: [], onboarding_status: [],
  state: [], city: [], country: [],
  existing_ota_connected: [], ota_name: [],
  interested_plan: [], setup_payment_status: [],
  rooms_range: { min: null, max: null }, fee_range: { min: null, max: null },
  has_company: null, has_gst: null, has_google_map: null, has_drive_link: null, has_pef_pdf: null,
  has_account_number: null, has_ifsc: null, has_cheque_pdf: null,
  bank_name: [], website_available: [],
};

const COLORS = ['#0d9488', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];
const STORAGE_KEY = 'leads_advanced_filters';

// --- Reusable Sub-Components ---

const KpiCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-lg transition-shadow" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-xl ${color}`}><Icon size={24} className="text-white" /></div>
    </div>
  </div>
);

const SectionWrapper = ({ id, title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`section-${id}`}>
      <button onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <Icon size={20} className="text-teal-600 flex-shrink-0" />
        <span className="font-semibold text-gray-800 flex-1">{title}</span>
        {open ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-gray-50">{children}</div>}
    </div>
  );
};

const MiniPie = ({ data, title, onItemClick, activeCrossValues = [] }) => {
  const filtered = data.filter(d => d.count > 0);
  if (filtered.length === 0) return <p className="text-sm text-gray-400 py-4 text-center">No data</p>;
  const total = filtered.reduce((s, d) => s + d.count, 0);
  const hasActive = activeCrossValues.length > 0;
  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-3">{title}</p>
      <div className="flex items-start gap-4">
        <ResponsiveContainer width={140} height={140}>
          <PieChart>
            <Pie data={filtered} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
              {filtered.map((entry, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]}
                  opacity={hasActive && !activeCrossValues.includes(entry.name) ? 0.3 : 1}
                  stroke={activeCrossValues.includes(entry.name) ? '#000' : 'none'}
                  strokeWidth={activeCrossValues.includes(entry.name) ? 2 : 0} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => v.toLocaleString()} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-1.5 max-h-[140px] overflow-y-auto">
          {filtered.map((item, i) => (
            <button key={item.name} onClick={() => onItemClick?.(item)}
              className="flex items-center gap-2 w-full text-left hover:bg-gray-50 rounded px-1.5 py-1 transition-colors group" data-testid={`pie-item-${title}-${item.name}`}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span className="text-xs text-gray-600 flex-1 truncate group-hover:text-gray-900">{item.name}</span>
              <span className="text-xs font-semibold text-gray-800">{item.count}</span>
              <span className="text-[10px] text-gray-400">({total > 0 ? Math.round((item.count / total) * 100) : 0}%)</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const MiniBar = ({ data, title, onItemClick, layout = 'horizontal', height = 200, activeCrossValues = [] }) => {
  const filtered = data.filter(d => d.count > 0).slice(0, 15);
  if (filtered.length === 0) return <p className="text-sm text-gray-400 py-4 text-center">No data</p>;
  const hasActive = activeCrossValues.length > 0;
  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={filtered} layout={layout === 'vertical' ? 'vertical' : 'horizontal'}
          onClick={(e) => { if (e?.activeLabel) { const item = filtered.find(d => d.name === e.activeLabel); if (item) onItemClick?.(item); } }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          {layout === 'vertical' ? (
            <>
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 10 }} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10 }} />
            </>
          )}
          <Tooltip formatter={(v) => v.toLocaleString()} />
          <Bar dataKey="count" radius={layout === 'vertical' ? [0, 4, 4, 0] : [4, 4, 0, 0]} cursor="pointer">
            {filtered.map((entry, i) => (
              <Cell key={entry.name} fill={activeCrossValues.includes(entry.name) ? '#0f766e' : '#0d9488'}
                opacity={hasActive && !activeCrossValues.includes(entry.name) ? 0.3 : 1}
                stroke={activeCrossValues.includes(entry.name) ? '#134e4a' : 'none'}
                strokeWidth={activeCrossValues.includes(entry.name) ? 2 : 0} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const BooleanStat = ({ data, title, onItemClick }) => {
  const yes = data.find(d => d.name === 'Yes')?.count || 0;
  const no = data.find(d => d.name === 'No')?.count || 0;
  const total = yes + no;
  const pct = total > 0 ? Math.round((yes / total) * 100) : 0;
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <p className="text-xs font-medium text-gray-600 mb-2">{title}</p>
      <div className="flex items-end gap-2 mb-2">
        <span className="text-2xl font-bold text-gray-900">{pct}%</span>
        <span className="text-xs text-gray-500 mb-1">{yes} of {total}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className="bg-teal-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => onItemClick?.({ name: 'Yes', count: yes })}
          className="text-[11px] text-teal-600 hover:underline" data-testid={`bool-yes-${title}`}>Yes: {yes}</button>
        <button onClick={() => onItemClick?.({ name: 'No', count: no })}
          className="text-[11px] text-gray-500 hover:underline" data-testid={`bool-no-${title}`}>No: {no}</button>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center">
    <p className="text-xl font-bold text-gray-900">{typeof value === 'number' ? (Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1)) : value || '-'}</p>
    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
  </div>
);

// --- Analytics key → display column mapping for dynamic drill-down ---
const LEAD_DISPLAY_MAP = {
  lead_stage: { label: 'Lead Stage', key: 'lead_stage' },
  lead_owner: { label: 'Lead Owner', key: 'lead_owner' },
  source: { label: 'Source', key: 'source' },
  property_status: { label: 'Property Status', key: 'property_status' },
  onboarding_status: { label: 'Onboarding Status', key: 'onboarding_status' },
  state: { label: 'State', key: 'state' },
  city: { label: 'City', key: 'city' },
  country: { label: 'Country', key: 'country' },
  website_available: { label: 'Website Available', key: 'website_available' },
  existing_ota_connected: { label: 'OTA Connected', key: 'existing_ota_connected' },
  interested_plan: { label: 'Interested Plan', key: 'interested_plan' },
  setup_payment_status: { label: 'Payment Status', key: 'setup_payment_status' },
  has_company_yes: { label: 'Has Company', key: '_static', staticValue: 'Yes' },
  has_company_no: { label: 'Has Company', key: '_static', staticValue: 'No' },
  has_gst_yes: { label: 'Has GST', key: '_static', staticValue: 'Yes' },
  has_gst_no: { label: 'Has GST', key: '_static', staticValue: 'No' },
  has_google_map_yes: { label: 'Google Map Link', key: '_static', staticValue: 'Yes' },
  has_google_map_no: { label: 'Google Map Link', key: '_static', staticValue: 'No' },
  has_drive_link_yes: { label: 'Drive Link', key: '_static', staticValue: 'Yes' },
  has_drive_link_no: { label: 'Drive Link', key: '_static', staticValue: 'No' },
  has_pef_pdf_yes: { label: 'PEF PDF', key: '_static', staticValue: 'Yes' },
  has_pef_pdf_no: { label: 'PEF PDF', key: '_static', staticValue: 'No' },
  has_account_number_yes: { label: 'Account Number', key: '_static', staticValue: 'Yes' },
  has_account_number_no: { label: 'Account Number', key: '_static', staticValue: 'No' },
  has_ifsc_yes: { label: 'IFSC Code', key: '_static', staticValue: 'Yes' },
  has_ifsc_no: { label: 'IFSC Code', key: '_static', staticValue: 'No' },
  has_cheque_pdf_yes: { label: 'Cancelled Cheque', key: '_static', staticValue: 'Yes' },
  has_cheque_pdf_no: { label: 'Cancelled Cheque', key: '_static', staticValue: 'No' },
  bank_name: { label: 'Bank Name', key: 'bank_name' },
  master_form: { label: 'Master Form', key: 'master_form' },
  noc_agreement: { label: 'NOC Agreement', key: 'noc_agreement' },
  lease_agreement: { label: 'Lease Agreement', key: 'lease_agreement' },
  electricity_bill: { label: 'Electricity Bill', key: 'electricity_bill' },
  pan_card: { label: 'PAN Card', key: 'pan_card' },
  aadhar_card: { label: 'Aadhar Card', key: 'aadhar_card' },
  gst_certificate: { label: 'GST Certificate', key: 'gst_certificate' },
  cancelled_cheque: { label: 'Cancelled Cheque Status', key: 'cancelled_cheque' },
  property_photos: { label: 'Property Photos', key: 'property_photos' },
  collection_status: { label: 'Collection Status', key: 'collection_status' },
  ota_names: { label: 'OTA Platform', key: '_static' },
  ota_statuses: { label: 'OTA Status', key: '_static' },
  ota_locked: { label: 'OTA Locked', key: '_static' },
  ota_timer_running: { label: 'Timer Running', key: '_static' },
  ota_access: { label: 'Hotel Access', key: '_static' },
};

function getLeadFieldValue(lead, analyticsKey, filterValue) {
  const display = LEAD_DISPLAY_MAP[analyticsKey];
  if (!display) return filterValue || '-';
  if (display.key === '_static') return display.staticValue || filterValue || '-';
  return lead[display.key] || '-';
}

function exportToExcel(data, nameField, colLabel, colAccessor, filename) {
  const rows = data.map(item => ({
    'Property Name': item[nameField] || '-',
    [colLabel]: colAccessor(item),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

// --- Drilldown Panel ---
const DrilldownPanel = ({ isOpen, data, title, subtitle, onClose, onFullView, loading, analyticsKey, filterValue }) => {
  const [search, setSearch] = useState('');
  if (!isOpen) return null;

  const display = LEAD_DISPLAY_MAP[analyticsKey] || { label: 'Value', key: '_static' };
  const colLabel = display.label;
  const getValue = (lead) => getLeadFieldValue(lead, analyticsKey, filterValue);

  const filtered = (data || []).filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.hotel_name || '').toLowerCase().includes(q) || String(getValue(l)).toLowerCase().includes(q);
  });

  const handleExport = () => {
    exportToExcel(filtered, 'hotel_name', colLabel, (item) => getLeadFieldValue(item, analyticsKey, filterValue), `leads_${analyticsKey}_${filterValue || 'all'}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="drilldown-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-800 text-base">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="drilldown-export-excel">
              <Download size={13} /> Export Excel
            </button>
            <button onClick={onFullView}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              data-testid="drilldown-full-view">
              <ExternalLink size={13} /> Open Full View
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid="drilldown-close">
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search in results..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none"
              data-testid="drilldown-search" />
          </div>
          <p className="text-xs text-gray-400 mt-1.5">{filtered.length} of {(data || []).length} results</p>
        </div>

        {/* Table - Dynamic 2 columns */}
        <div className="flex-1 overflow-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Search size={32} className="mx-auto mb-2 text-gray-300" />
              <p>No matching leads found</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-12">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Property Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">{colLabel}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((lead, i) => (
                  <tr key={lead.id || i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-gray-400">{i + 1}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{lead.hotel_name || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{getValue(lead)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Field-to-filter mapping for drilldown ---
const FIELD_MAP = {
  lead_stage: { field: 'lead_stage', op: 'eq' },
  lead_owner: { field: 'lead_owner', op: 'eq' },
  source: { field: 'source', op: 'eq' },
  property_status: { field: 'property_status', op: 'eq' },
  onboarding_status: { field: 'onboarding_status', op: 'eq' },
  state: { field: 'state', op: 'eq' },
  city: { field: 'city', op: 'eq' },
  country: { field: 'country', op: 'eq' },
  website_available: { field: 'website_available', op: 'eq' },
  existing_ota_connected: { field: 'existing_ota_connected', op: 'eq' },
  interested_plan: { field: 'payment_info.interested_plan', op: 'eq' },
  setup_payment_status: { field: 'payment_info.setup_payment_status', op: 'eq' },
  has_company_yes: { field: 'property_details.company_name', op: 'exists' },
  has_company_no: { field: 'property_details.company_name', op: 'not_exists' },
  has_gst_yes: { field: 'property_details.gst_no', op: 'exists' },
  has_gst_no: { field: 'property_details.gst_no', op: 'not_exists' },
  has_google_map_yes: { field: 'document_collection.google_map_link', op: 'exists' },
  has_google_map_no: { field: 'document_collection.google_map_link', op: 'not_exists' },
  has_drive_link_yes: { field: 'document_collection.document_drive_link_updated_time', op: 'exists' },
  has_drive_link_no: { field: 'document_collection.document_drive_link_updated_time', op: 'not_exists' },
  has_pef_pdf_yes: { field: 'documentCollection.pefPdfUrl', op: 'exists' },
  has_pef_pdf_no: { field: 'documentCollection.pefPdfUrl', op: 'not_exists' },
  has_account_number_yes: { field: 'bankDetails.accountNumber', op: 'exists' },
  has_account_number_no: { field: 'bankDetails.accountNumber', op: 'not_exists' },
  has_ifsc_yes: { field: 'bankDetails.ifscCode', op: 'exists' },
  has_ifsc_no: { field: 'bankDetails.ifscCode', op: 'not_exists' },
  has_cheque_pdf_yes: { field: 'bankDetails.cancelledChequePdfUrl', op: 'exists' },
  has_cheque_pdf_no: { field: 'bankDetails.cancelledChequePdfUrl', op: 'not_exists' },
  bank_name: { field: 'bankDetails.bankName', op: 'eq' },
  // Checklist fields
  master_form: { field: 'property_checklist_info.master_form', op: 'eq' },
  noc_agreement: { field: 'property_checklist_info.noc_agreement', op: 'eq' },
  lease_agreement: { field: 'property_checklist_info.lease_agreement', op: 'eq' },
  electricity_bill: { field: 'property_checklist_info.electricity_bill', op: 'eq' },
  pan_card: { field: 'property_checklist_info.pan_card', op: 'eq' },
  aadhar_card: { field: 'property_checklist_info.aadhar_card', op: 'eq' },
  gst_certificate: { field: 'property_checklist_info.gst_certificate', op: 'eq' },
  cancelled_cheque: { field: 'property_checklist_info.cancelled_check_and_bank_details', op: 'eq' },
  property_photos: { field: 'property_checklist_info.property_photos', op: 'eq' },
  collection_status: { field: 'property_checklist_info.document_collection_status', op: 'eq' },
  // OTA array fields
  ota_names: { field: 'ota_info.ota_name', op: 'array_contains' },
  ota_statuses: { field: 'ota_info.ota_status', op: 'array_contains' },
  ota_locked: { field: 'ota_info.locked', op: 'array_contains' },
  ota_timer_running: { field: 'ota_info.timer_running', op: 'array_contains' },
  ota_access: { field: 'ota_info.access_to_hotel', op: 'array_contains' },
};

// Maps analytics key to the Leads page advanced filter format
function buildLeadsFilter(analyticsKey, itemName) {
  const mapping = FIELD_MAP[analyticsKey];
  if (!mapping) return null;

  // For boolean fields, analyticsKey ends with _yes or _no
  if (mapping.op === 'exists') return { [mapping.field]: { op: 'exists', value: true } };
  if (mapping.op === 'not_exists') return { [mapping.field]: { op: 'not_exists', value: true } };
  if (mapping.op === 'eq') return { [mapping.field]: { op: 'eq', value: itemName } };
  if (mapping.op === 'array_contains') {
    const subField = mapping.field.split('.').pop();
    return { [mapping.field]: { op: 'array_contains', value: { [subField]: itemName } } };
  }
  return null;
}

// --- Main Component ---
const TABS = [
  { id: 'lead-info', label: 'Lead Information', icon: Users },
  { id: 'geographical', label: 'Geographical', icon: MapPin },
  { id: 'offboarded', label: 'Offboarded', icon: Layers },
];

const EMPTY_CROSS_FILTERS = {
  city: [], state: [], offboarded_status: [], offboarded_reason: [], date_range: { start: '', end: '' },
  // Lead Info section keys
  lead_stage: [], lead_owner: [], source: [], property_status: [], onboarding_status: [],
  // OTA & Payment keys
  existing_ota_connected: [], ota_name: [], interested_plan: [], setup_payment_status: [],
  // Bank
  bank_name: [],
  // Address
  country: [], website_available: [],
};

const CROSS_FILTER_LABELS = {
  city: { prefix: 'City', color: 'bg-blue-50 text-blue-700' },
  state: { prefix: 'State', color: 'bg-teal-50 text-teal-700' },
  offboarded_status: { prefix: 'Status', color: 'bg-emerald-50 text-emerald-700' },
  offboarded_reason: { prefix: 'Reason', color: 'bg-orange-50 text-orange-700' },
  lead_stage: { prefix: 'Stage', color: 'bg-purple-50 text-purple-700' },
  lead_owner: { prefix: 'Owner', color: 'bg-sky-50 text-sky-700' },
  source: { prefix: 'Source', color: 'bg-amber-50 text-amber-700' },
  property_status: { prefix: 'Prop Status', color: 'bg-lime-50 text-lime-700' },
  onboarding_status: { prefix: 'Onboarding', color: 'bg-pink-50 text-pink-700' },
  existing_ota_connected: { prefix: 'OTA Connected', color: 'bg-cyan-50 text-cyan-700' },
  ota_name: { prefix: 'OTA', color: 'bg-indigo-50 text-indigo-700' },
  interested_plan: { prefix: 'Plan', color: 'bg-rose-50 text-rose-700' },
  setup_payment_status: { prefix: 'Payment', color: 'bg-fuchsia-50 text-fuchsia-700' },
  bank_name: { prefix: 'Bank', color: 'bg-yellow-50 text-yellow-700' },
  country: { prefix: 'Country', color: 'bg-teal-50 text-teal-700' },
  website_available: { prefix: 'Website', color: 'bg-gray-100 text-gray-700' },
};

// ─── Cross-Filter Bar ──────────────────────────
const CrossFilterBar = ({ crossFilters, onChange, onReset, onResetAll, hasGlobalFilters }) => {
  const tags = [];
  const arrayKeys = Object.keys(CROSS_FILTER_LABELS);
  arrayKeys.forEach(key => {
    if (key === 'date_range') return;
    (crossFilters[key] || []).forEach(v => {
      const cfg = CROSS_FILTER_LABELS[key] || { prefix: key, color: 'bg-gray-100 text-gray-700' };
      tags.push({ key, value: v, label: `${cfg.prefix}: ${v}`, color: cfg.color });
    });
  });
  if (crossFilters.date_range?.start) tags.push({ key: 'date_start', value: crossFilters.date_range.start, label: `From: ${crossFilters.date_range.start}`, color: 'bg-violet-50 text-violet-700' });
  if (crossFilters.date_range?.end) tags.push({ key: 'date_end', value: crossFilters.date_range.end, label: `To: ${crossFilters.date_range.end}`, color: 'bg-violet-50 text-violet-700' });

  if (tags.length === 0 && !hasGlobalFilters) return null;

  const removeTag = (tag) => {
    if (tag.key === 'date_start') {
      onChange({ ...crossFilters, date_range: { ...crossFilters.date_range, start: '' } });
    } else if (tag.key === 'date_end') {
      onChange({ ...crossFilters, date_range: { ...crossFilters.date_range, end: '' } });
    } else {
      onChange({ ...crossFilters, [tag.key]: (crossFilters[tag.key] || []).filter(v => v !== tag.value) });
    }
  };

  return (
    <div className="bg-white rounded-xl border-2 border-indigo-100 shadow-sm px-4 py-3" data-testid="cross-filter-bar">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="p-1 bg-indigo-100 rounded-md"><Filter size={13} className="text-indigo-600" /></div>
          <span className="text-xs font-semibold text-indigo-700 whitespace-nowrap">Cross-Filters</span>
        </div>
        <div className="flex flex-wrap gap-1.5 flex-1">
          {tags.map((tag, i) => (
            <span key={`${tag.key}-${tag.value}-${i}`}
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md ${tag.color}`}>
              {tag.label}
              <button onClick={() => removeTag(tag)} className="hover:opacity-70" data-testid={`cross-filter-remove-${tag.key}-${tag.value}`}>
                <X size={11} />
              </button>
            </span>
          ))}
          {tags.length === 0 && hasGlobalFilters && (
            <span className="text-[11px] text-gray-400 italic">No cross-filters active (global filters applied)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {tags.length > 0 && (
            <button onClick={onReset}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
              data-testid="cross-filter-reset">
              <X size={12} /> Reset Cross
            </button>
          )}
          {(tags.length > 0 || hasGlobalFilters) && onResetAll && (
            <button onClick={onResetAll}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              data-testid="reset-all-filters">
              <RotateCcw size={12} /> Reset All Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const LeadAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('lead-info');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [filterOptions, setFilterOptions] = useState({});
  const [drilldown, setDrilldown] = useState({ open: false, data: [], title: '', subtitle: '', loading: false, filterKey: '', filterValue: '' });
  const [crossFilters, setCrossFilters] = useState({ ...EMPTY_CROSS_FILTERS });

  const hasCrossFilters = useMemo(() => {
    return Object.entries(crossFilters).some(([key, val]) => {
      if (key === 'date_range') return !!val?.start || !!val?.end;
      return Array.isArray(val) && val.length > 0;
    });
  }, [crossFilters]);

  const toggleCrossFilter = useCallback((key, value) => {
    setCrossFilters(prev => {
      const current = prev[key] || [];
      const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, [key]: updated };
    });
  }, []);

  const resetCrossFilters = useCallback(() => {
    setCrossFilters({ ...EMPTY_CROSS_FILTERS });
  }, []);

  // ─── Time Comparison state ──────────────────
  const [tcPeriods, setTcPeriods] = useState([
    { label: '', from: '', to: '' },
    { label: '', from: '', to: '' },
  ]);
  const [tcResults, setTcResults] = useState(null);
  const [tcLoading, setTcLoading] = useState(false);
  const [tcActive, setTcActive] = useState(false);
  const [tcPreset, setTcPreset] = useState('custom');

  const updatePeriod = useCallback((idx, field, value) => {
    setTcPeriods(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  }, []);

  const addPeriod = useCallback(() => {
    if (tcPeriods.length < 4) setTcPeriods(prev => [...prev, { label: '', from: '', to: '' }]);
  }, [tcPeriods.length]);

  const removePeriod = useCallback((idx) => {
    if (tcPeriods.length > 2) setTcPeriods(prev => prev.filter((_, i) => i !== idx));
  }, [tcPeriods.length]);

  const applyPreset = useCallback((preset) => {
    setTcPreset(preset);
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    if (preset === 'year-vs-year') {
      setTcPeriods([
        { label: `${y - 1}`, from: `${y - 1}-01-01`, to: `${y - 1}-12-31` },
        { label: `${y}`, from: `${y}-01-01`, to: `${y}-12-31` },
      ]);
    } else if (preset === 'month-vs-month') {
      const prev = m === 0 ? 11 : m - 1;
      const prevY = m === 0 ? y - 1 : y;
      const currStart = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const currEnd = new Date(y, m + 1, 0).toISOString().split('T')[0];
      const prevStart = `${prevY}-${String(prev + 1).padStart(2, '0')}-01`;
      const prevEnd = new Date(prevY, prev + 1, 0).toISOString().split('T')[0];
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      setTcPeriods([
        { label: `${monthNames[prev]} ${prevY}`, from: prevStart, to: prevEnd },
        { label: `${monthNames[m]} ${y}`, from: currStart, to: currEnd },
      ]);
    } else if (preset === 'last-30-days') {
      const end1 = new Date(now); end1.setDate(end1.getDate() - 1);
      const start1 = new Date(end1); start1.setDate(start1.getDate() - 29);
      const end0 = new Date(start1); end0.setDate(end0.getDate() - 1);
      const start0 = new Date(end0); start0.setDate(start0.getDate() - 29);
      const fmt = d => d.toISOString().split('T')[0];
      setTcPeriods([
        { label: 'Previous 30 days', from: fmt(start0), to: fmt(end0) },
        { label: 'Last 30 days', from: fmt(start1), to: fmt(end1) },
      ]);
    } else if (preset === 'last-7-days') {
      const end1 = new Date(now); end1.setDate(end1.getDate() - 1);
      const start1 = new Date(end1); start1.setDate(start1.getDate() - 6);
      const end0 = new Date(start1); end0.setDate(end0.getDate() - 1);
      const start0 = new Date(end0); start0.setDate(start0.getDate() - 6);
      const fmt = d => d.toISOString().split('T')[0];
      setTcPeriods([
        { label: 'Previous 7 days', from: fmt(start0), to: fmt(end0) },
        { label: 'Last 7 days', from: fmt(start1), to: fmt(end1) },
      ]);
    }
  }, []);

  // Count active filters
  const activeFilterCount = [
    filters.search,
    filters.date_range?.start, filters.date_range?.end,
    ...(filters.lead_stage || []), ...(filters.lead_owner || []), ...(filters.source || []),
    ...(filters.property_status || []), ...(filters.onboarding_status || []),
    ...(filters.state || []), ...(filters.city || []),
    ...(filters.existing_ota_connected || []), ...(filters.ota_name || []),
    ...(filters.interested_plan || []), ...(filters.setup_payment_status || []),
    filters.has_company, filters.has_gst, filters.has_google_map, filters.has_drive_link, filters.has_pef_pdf,
    filters.has_account_number, filters.has_ifsc, filters.has_cheque_pdf,
    ...(filters.bank_name || []),
    filters.rooms_range?.min, filters.rooms_range?.max,
    filters.fee_range?.min, filters.fee_range?.max,
  ].filter(Boolean).length;

  // Build clean filter payload (strip empty values)
  const buildPayload = useCallback((f) => {
    const payload = {};
    if (f.date_range?.start || f.date_range?.end) payload.date_range = f.date_range;
    if (f.search) payload.search = f.search;
    const multiKeys = ['lead_stage','lead_owner','source','property_status','onboarding_status','state','city','country','existing_ota_connected','ota_name','interested_plan','setup_payment_status','bank_name','website_available'];
    multiKeys.forEach(k => { if (f[k]?.length) payload[k] = f[k]; });
    const boolKeys = ['has_company','has_gst','has_google_map','has_drive_link','has_pef_pdf','has_account_number','has_ifsc','has_cheque_pdf'];
    boolKeys.forEach(k => { if (f[k]) payload[k] = f[k]; });
    if (f.rooms_range?.min || f.rooms_range?.max) payload.rooms_range = f.rooms_range;
    if (f.fee_range?.min || f.fee_range?.max) payload.fee_range = f.fee_range;
    return payload;
  }, []);

  const executeTimeComparison = useCallback(async () => {
    const valid = tcPeriods.filter(p => p.from && p.to);
    if (valid.length < 2) return;
    setTcLoading(true);
    try {
      const basePayload = buildPayload(filters);
      const payload = mergeCrossIntoPayload(basePayload, crossFilters);
      const res = await leadAnalyticsAPI.timeComparison({
        periods: valid.map(p => ({ label: p.label || `${p.from} to ${p.to}`, from: p.from, to: p.to })),
        filters: payload,
      });
      setTcResults(res.data?.periods || []);
      setTcActive(true);
    } catch (err) {
      console.error('Time comparison failed:', err);
    }
    setTcLoading(false);
  }, [tcPeriods, filters, buildPayload]);

  const resetTimeComparison = useCallback(() => {
    setTcPeriods([{ label: '', from: '', to: '' }, { label: '', from: '', to: '' }]);
    setTcResults(null);
    setTcActive(false);
    setTcPreset('custom');
  }, []);

  // Merge crossFilters into filter payload for backend
  const mergeCrossIntoPayload = useCallback((payload, cf) => {
    const merged = { ...payload };
    const arrayKeys = ['city','state','lead_stage','lead_owner','source','property_status','onboarding_status',
      'existing_ota_connected','ota_name','interested_plan','setup_payment_status','bank_name','country','website_available'];
    arrayKeys.forEach(key => {
      const crossVals = cf[key] || [];
      if (crossVals.length > 0) {
        merged[key] = [...new Set([...(merged[key] || []), ...crossVals])];
      }
    });
    if (cf.date_range?.start && !merged.date_range?.start) {
      merged.date_range = { ...(merged.date_range || {}), start: cf.date_range.start };
    }
    if (cf.date_range?.end && !merged.date_range?.end) {
      merged.date_range = { ...(merged.date_range || {}), end: cf.date_range.end };
    }
    return merged;
  }, []);

  const loadAnalytics = useCallback(async (f, cf) => {
    try {
      setLoading(true);
      const basePayload = buildPayload(f || filters);
      const payload = mergeCrossIntoPayload(basePayload, cf || crossFilters);
      const hasFilters = Object.keys(payload).length > 0;
      const res = hasFilters
        ? await leadAnalyticsAPI.getFiltered(payload)
        : await leadAnalyticsAPI.getComprehensive();
      setAnalytics(res.data);

      // Extract filter options from unfiltered data (only on first load)
      if (!hasFilters && res.data) {
        const d = res.data;
        setFilterOptions({
          lead_stage: (d.lead_info?.lead_stage || []).map(x => x.name).filter(x => x !== 'Not Set'),
          lead_owner: (d.lead_info?.lead_owner || []).map(x => x.name).filter(x => x !== 'Not Set'),
          source: (d.lead_info?.source || []).map(x => x.name).filter(x => x !== 'Not Set'),
          property_status: (d.lead_info?.property_status || []).map(x => x.name).filter(x => x !== 'Not Set'),
          onboarding_status: (d.lead_info?.onboarding_status || []).map(x => x.name).filter(x => x !== 'Not Set'),
          state: (d.address?.state || []).map(x => x.name).filter(x => x !== 'Not Set'),
          city: (d.address?.city || []).map(x => x.name).filter(x => x !== 'Not Set'),
          existing_ota_connected: (d.ota_challenges?.existing_ota_connected || []).map(x => x.name).filter(x => x !== 'Not Set'),
          ota_name: (d.ota_info?.ota_names || []).map(x => x.name).filter(x => x !== 'Not Set'),
          interested_plan: (d.payment?.interested_plan || []).map(x => x.name).filter(x => x !== 'Not Set'),
          setup_payment_status: (d.payment?.setup_payment_status || []).map(x => x.name).filter(x => x !== 'Not Set'),
          bank_name: (d.bank?.bank_name || []).map(x => x.name).filter(x => x !== 'Not Set'),
        });
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, crossFilters, buildPayload, mergeCrossIntoPayload]);

  useEffect(() => { loadAnalytics(); }, []);

  // Re-fetch when crossFilters change
  useEffect(() => { loadAnalytics(filters, crossFilters); }, [crossFilters]);

  const applyFilters = useCallback(() => { loadAnalytics(filters, crossFilters); }, [filters, crossFilters, loadAnalytics]);

  const resetFilters = useCallback(() => {
    const empty = { ...EMPTY_FILTERS };
    setFilters(empty);
    loadAnalytics(empty, crossFilters);
  }, [loadAnalytics, crossFilters]);

  const resetAll = useCallback(() => {
    const emptyF = { ...EMPTY_FILTERS };
    const emptyCF = { ...EMPTY_CROSS_FILTERS };
    setFilters(emptyF);
    setCrossFilters(emptyCF);
    loadAnalytics(emptyF, emptyCF);
  }, [loadAnalytics]);

  // Chart click → toggle cross-filter AND open drilldown
  const handleChartClickFilter = useCallback((analyticsKey, item, sectionLabel) => {
    // Map analytics keys to cross-filter keys
    const chartToCross = {
      lead_stage: 'lead_stage', lead_owner: 'lead_owner', source: 'source',
      property_status: 'property_status', onboarding_status: 'onboarding_status',
      state: 'state', city: 'city', country: 'country',
      existing_ota_connected: 'existing_ota_connected',
      interested_plan: 'interested_plan', setup_payment_status: 'setup_payment_status',
      ota_names: 'ota_name', bank_name: 'bank_name', website_available: 'website_available',
    };
    const crossKey = chartToCross[analyticsKey];
    if (crossKey && item.name && item.name !== 'Not Set') {
      toggleCrossFilter(crossKey, item.name);
    }
    // Always open drilldown
    openDrilldown(analyticsKey, item, sectionLabel);
  }, [toggleCrossFilter]);

  const openDrilldown = useCallback(async (analyticsKey, item, sectionLabel) => {
    const mapping = FIELD_MAP[analyticsKey];
    if (!mapping) return;
    const value = item.name === 'Not Set' ? '' : String(item.name);

    setDrilldown({ open: true, data: [], title: `${sectionLabel}: ${item.name}`, subtitle: `${item.count} leads`, loading: true, filterKey: analyticsKey, filterValue: item.name });

    try {
      let op = mapping.op;
      let queryValue = value;
      if (value === 'Not Set' || value === '') {
        op = 'null';
        queryValue = '';
      }
      // Use unified filter: globalFilters + crossFilters + drilldown field
      const basePayload = buildPayload(filters);
      const unifiedFilters = mergeCrossIntoPayload(basePayload, crossFilters);
      const res = await leadAnalyticsAPI.postDrilldown({
        filters: unifiedFilters,
        drilldown: { field: mapping.field, value: queryValue, op },
      });
      setDrilldown(prev => ({ ...prev, data: res.data.data || [], loading: false, subtitle: `${res.data.total} leads` }));
    } catch (err) {
      console.error('Drilldown failed:', err);
      setDrilldown(prev => ({ ...prev, data: [], loading: false }));
    }
  }, [filters, crossFilters, buildPayload, mergeCrossIntoPayload]);

  const handleFullView = useCallback(() => {
    // Build unified filter: global + cross + drilldown field
    const basePayload = buildPayload(filters);
    const unified = mergeCrossIntoPayload(basePayload, crossFilters);
    const drilldownFilter = buildLeadsFilter(drilldown.filterKey, drilldown.filterValue);

    // Combine both for the Leads page
    const combinedFilter = { ...unified };
    if (drilldownFilter) {
      Object.assign(combinedFilter, drilldownFilter);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(combinedFilter));
    localStorage.setItem('leads_filter_open', 'true');
    navigate('/leads');
  }, [drilldown, filters, crossFilters, buildPayload, mergeCrossIntoPayload, navigate]);

  const closeDrilldown = useCallback(() => {
    setDrilldown({ open: false, data: [], title: '', subtitle: '', loading: false, filterKey: '', filterValue: '' });
  }, []);

  // Helper to create a click handler that BOTH filters globally AND opens drilldown
  const makeClick = (analyticsKey, sectionLabel) => (item) => handleChartClickFilter(analyticsKey, item, sectionLabel);
  const makeBoolClick = (baseKey, sectionLabel) => (item) => {
    const key = item.name === 'Yes' ? `${baseKey}_yes` : `${baseKey}_no`;
    openDrilldown(key, item, sectionLabel);
  };
  const makeChecklistClick = (checklistKey, label) => (item) => openDrilldown(checklistKey, item, label);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="analytics-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center py-16 text-gray-400">Failed to load analytics data</div>;
  }

  const { kpis, lead_info, address, ota_challenges, payment, property_details, documents, pef, checklist, bank, ota_info } = analytics;
  const payStats = payment.stats || {};
  const occStats = property_details.occupancy_stats || {};
  const roomStats = ota_challenges.rooms_stats || {};

  return (
    <div className="space-y-5 max-w-full" data-testid="lead-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Hotel Onboarded Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Unified analytics across leads, geography, and offboarding</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1" data-testid="analytics-tabs">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid={`tab-${tab.id}`}>
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Cross-Filter Bar (shown across all tabs when active) */}
      {(hasCrossFilters || activeFilterCount > 0) && (
        <CrossFilterBar
          crossFilters={crossFilters}
          onChange={setCrossFilters}
          onReset={resetCrossFilters}
          onResetAll={resetAll}
          hasGlobalFilters={activeFilterCount > 0}
        />
      )}

      {/* Time Comparison Bar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="time-comparison-bar">
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarIcon size={15} className="text-gray-500" />
              <span className="text-xs font-semibold text-gray-700">Time Comparison (Closed Won Date)</span>
              {tcActive && <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>}
            </div>
            <div className="flex items-center gap-2">
              {/* Presets */}
              {[
                { id: 'year-vs-year', label: 'Year vs Year' },
                { id: 'month-vs-month', label: 'Month vs Month' },
                { id: 'last-30-days', label: 'Last 30 Days' },
                { id: 'last-7-days', label: 'Last 7 Days' },
                { id: 'custom', label: 'Custom' },
              ].map(pr => (
                <button key={pr.id} onClick={() => applyPreset(pr.id)}
                  className={`px-2.5 py-1 text-[10px] font-medium rounded-lg transition-colors ${
                    tcPreset === pr.id ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`} data-testid={`tc-preset-${pr.id}`}>
                  {pr.label}
                </button>
              ))}
            </div>
          </div>

          {/* Period rows */}
          <div className="space-y-2">
            {tcPeriods.map((period, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`tc-period-${idx}`}>
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ['#0d9488','#3b82f6','#f59e0b','#ef4444'][idx] }} />
                <input type="text" value={period.label} onChange={e => updatePeriod(idx, 'label', e.target.value)}
                  placeholder={`Period ${idx + 1} label`}
                  className="w-36 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid={`tc-period-${idx}-label`} />
                <input type="date" value={period.from} onChange={e => updatePeriod(idx, 'from', e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid={`tc-period-${idx}-from`} />
                <span className="text-gray-400 text-xs">to</span>
                <input type="date" value={period.to} onChange={e => updatePeriod(idx, 'to', e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-400 outline-none"
                  data-testid={`tc-period-${idx}-to`} />
                {tcPeriods.length > 2 && (
                  <button onClick={() => removePeriod(idx)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`tc-remove-${idx}`}>
                    <Minus size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {tcPeriods.length < 4 && (
              <button onClick={addPeriod} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" data-testid="tc-add-period">
                <Plus size={13} /> Add Period
              </button>
            )}
            <button onClick={executeTimeComparison} disabled={tcLoading || tcPeriods.filter(p => p.from && p.to).length < 2}
              className="flex items-center gap-1 px-4 py-1.5 text-xs font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
              data-testid="tc-apply">
              {tcLoading ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Compare
            </button>
            {tcActive && (
              <button onClick={resetTimeComparison}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-500 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors"
                data-testid="tc-reset">
                <RotateCcw size={12} /> Reset Comparison
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Bar (visible on Lead Information tab when time comparison is NOT active) */}
      {activeTab === 'lead-info' && !tcActive && (
        <AnalyticsFilterBar
          filters={filters}
          onChange={setFilters}
          onApply={applyFilters}
          onReset={resetFilters}
          filterOptions={filterOptions}
          activeCount={activeFilterCount}
        />
      )}

      {/* Time Comparison Results (when active, replaces normal tab content) */}
      {tcActive && tcResults && (
        <TimeComparisonResults periods={tcResults} activeTab={activeTab} />
      )}

      {/* Normal Tab Content (only when time comparison is NOT active) */}
      {!tcActive && (
        <>
          {/* Tab Content: Lead Information */}
          {activeTab === 'lead-info' && analytics && (
            <LeadInfoContent
              analytics={analytics}
              makeClick={makeClick}
              makeBoolClick={makeBoolClick}
              makeChecklistClick={makeChecklistClick}
              openDrilldown={openDrilldown}
              crossFilters={crossFilters}
            />
          )}

          {/* Tab Content: Geographical */}
          {activeTab === 'geographical' && (
            <GeoPropertyAnalytics
              crossFilters={crossFilters}
              onCrossFilterToggle={toggleCrossFilter}
            />
          )}

          {/* Tab Content: Offboarded */}
          {activeTab === 'offboarded' && (
            <OffboardedAnalytics
              crossFilters={crossFilters}
              onCrossFilterToggle={toggleCrossFilter}
            />
          )}
        </>
      )}

      {/* Drilldown Panel (only active for Lead Info tab) */}
      <DrilldownPanel
        isOpen={drilldown.open}
        data={drilldown.data}
        title={drilldown.title}
        subtitle={drilldown.subtitle}
        loading={drilldown.loading}
        onClose={closeDrilldown}
        onFullView={handleFullView}
        analyticsKey={drilldown.filterKey}
        filterValue={drilldown.filterValue}
      />
    </div>
  );
};

// --- Lead Info Content (extracted for clarity) ---
const LeadInfoContent = ({ analytics, makeClick, makeBoolClick, makeChecklistClick, openDrilldown, crossFilters = {} }) => {
  const { kpis, lead_info, address, ota_challenges, payment, property_details, documents, pef, checklist, bank, ota_info, labels: labelData } = analytics;
  const payStats = payment.stats || {};
  const occStats = property_details.occupancy_stats || {};
  const roomStats = ota_challenges.rooms_stats || {};
  const navigate = useNavigate();
  const labelDist = labelData?.distribution || [];

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <KpiCard title="Total Leads" value={kpis.total_leads} icon={Users} color="bg-teal-600" subtitle="All Hotel Onboarded entries" />
        <KpiCard title="Closed Won" value={kpis.closed_won} icon={CheckCircle} color="bg-emerald-600" subtitle={`${kpis.total_leads > 0 ? Math.round((kpis.closed_won / kpis.total_leads) * 100) : 0}% conversion`} />
        <KpiCard title="OTA Active" value={kpis.leads_with_ota} icon={Globe2} color="bg-blue-600" subtitle="Leads with OTA entries" />
        <KpiCard title="Payment Done" value={kpis.payment_completed} icon={CreditCard} color="bg-violet-600" subtitle="Setup payment completed" />
      </div>

      {/* Section 1: Lead Information */}
      <SectionWrapper id="lead-info" title="Lead Information" icon={Users} defaultOpen={true}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <MiniPie data={lead_info.lead_stage} title="Lead Stage Distribution" onItemClick={makeClick('lead_stage', 'Lead Stage')} activeCrossValues={crossFilters.lead_stage || []} />
          <MiniBar data={lead_info.lead_owner} title="Leads by Owner" onItemClick={makeClick('lead_owner', 'Lead Owner')} layout="vertical" height={180} activeCrossValues={crossFilters.lead_owner || []} />
          <MiniPie data={lead_info.source} title="Lead Source" onItemClick={makeClick('source', 'Source')} activeCrossValues={crossFilters.source || []} />
          <MiniPie data={lead_info.property_status} title="Property Status" onItemClick={makeClick('property_status', 'Property Status')} activeCrossValues={crossFilters.property_status || []} />
          <MiniPie data={lead_info.onboarding_status} title="Onboarding Status" onItemClick={makeClick('onboarding_status', 'Onboarding Status')} activeCrossValues={crossFilters.onboarding_status || []} />
        </div>
      </SectionWrapper>

      {/* Label Distribution */}
      {labelDist.length > 0 && (
        <SectionWrapper id="labels" title="Label Distribution" icon={Tag} defaultOpen={true}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
            <MiniBar
              data={labelDist}
              title="Leads by Label"
              onItemClick={(item) => {
                const lid = item?.label_id;
                if (lid) navigate(`/leads?labels=${lid}`);
              }}
              layout="vertical"
              height={Math.max(180, labelDist.length * 32)}
            />
            <MiniPie
              data={labelDist}
              title="Label Share"
              onItemClick={(item) => {
                const lid = item?.label_id;
                if (lid) navigate(`/leads?labels=${lid}`);
              }}
            />
          </div>
        </SectionWrapper>
      )}

      {/* Section 2: Address Information */}
      <SectionWrapper id="address" title="Address Information" icon={MapPin}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <MiniBar data={address.state.slice(0, 15)} title="Top States" onItemClick={makeClick('state', 'State')} layout="vertical" height={250} activeCrossValues={crossFilters.state || []} />
          <MiniBar data={address.city.slice(0, 15)} title="Top Cities" onItemClick={makeClick('city', 'City')} layout="vertical" height={250} activeCrossValues={crossFilters.city || []} />
          <div className="space-y-4">
            <MiniPie data={address.website_available} title="Website Available" onItemClick={makeClick('website_available', 'Website Available')} activeCrossValues={crossFilters.website_available || []} />
            <MiniPie data={address.country.filter(c => c.name !== 'Not Set')} title="Country" onItemClick={makeClick('country', 'Country')} activeCrossValues={crossFilters.country || []} />
          </div>
        </div>
      </SectionWrapper>

      {/* Section 3: OTA & Challenges */}
      <SectionWrapper id="ota-challenges" title="OTA & Challenges" icon={Globe2}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <MiniPie data={ota_challenges.existing_ota_connected} title="Existing OTA Status" onItemClick={makeClick('existing_ota_connected', 'OTA Connected')} activeCrossValues={crossFilters.existing_ota_connected || []} />
          <MiniBar data={ota_challenges.rooms_distribution} title="Rooms Distribution" height={180} />
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Room Statistics</p>
            <div className="grid grid-cols-2 gap-2">
              <StatCard label="Avg Rooms" value={roomStats.avg} />
              <StatCard label="Min Rooms" value={roomStats.min} />
              <StatCard label="Max Rooms" value={roomStats.max} />
              <StatCard label="With Data" value={roomStats.with_data} />
            </div>
          </div>
          <MiniBar data={ota_challenges.properties_distribution} title="Properties per Lead" height={180} />
        </div>
      </SectionWrapper>

      {/* Section 4: Commercials */}
      <SectionWrapper id="payment" title="Commercials" icon={CreditCard}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <MiniPie data={payment.interested_plan} title="Interested Plan" onItemClick={makeClick('interested_plan', 'Interested Plan')} activeCrossValues={crossFilters.interested_plan || []} />
          <MiniPie data={payment.setup_payment_status} title="Setup Payment Status" onItemClick={makeClick('setup_payment_status', 'Payment Status')} activeCrossValues={crossFilters.setup_payment_status || []} />
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Payment Stats</p>
            <div className="grid grid-cols-2 gap-2">
              <StatCard label="Total Setup Fee" value={payStats.total_setup_fee ? `${Math.round(payStats.total_setup_fee / 1000)}K` : '-'} sub={`${payStats.with_fee || 0} leads`} />
              <StatCard label="Avg Setup Fee" value={payStats.avg_setup_fee ? Math.round(payStats.avg_setup_fee).toLocaleString() : '-'} />
              <StatCard label="Total Token" value={payStats.total_token ? `${Math.round(payStats.total_token / 1000)}K` : '-'} sub={`${payStats.with_token || 0} leads`} />
              <StatCard label="Total Pending" value={payStats.total_pending ? `${Math.round(payStats.total_pending / 1000)}K` : '-'} sub={`${payStats.with_pending || 0} leads`} />
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* Section 5: Property Details */}
      <SectionWrapper id="property-details" title="Property Details" icon={Building2}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 pt-4">
          <BooleanStat data={property_details.has_company} title="Has Company Name" onItemClick={makeBoolClick('has_company', 'Company Name')} />
          <BooleanStat data={property_details.has_gst} title="Has GST Number" onItemClick={makeBoolClick('has_gst', 'GST Number')} />
          <StatCard label="Avg Observed Occupancy" value={occStats.avg_observed ? `${Math.round(occStats.avg_observed)}%` : '-'} sub={`${occStats.with_observed || 0} leads`} />
          <StatCard label="Avg Expected Occupancy" value={occStats.avg_expected ? `${Math.round(occStats.avg_expected)}%` : '-'} sub={`${occStats.with_expected || 0} leads`} />
        </div>
      </SectionWrapper>

      {/* Section 6: Document Collection */}
      <SectionWrapper id="documents" title="Document Collection" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pt-4">
          <BooleanStat data={documents.has_google_map} title="Has Google Map Link" onItemClick={makeBoolClick('has_google_map', 'Google Map Link')} />
          <BooleanStat data={documents.has_drive_link} title="Has Drive Link Updated" onItemClick={makeBoolClick('has_drive_link', 'Drive Link')} />
        </div>
      </SectionWrapper>

      {/* Section 7: PEF Document */}
      <SectionWrapper id="pef" title="PEF Document" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
          <BooleanStat data={pef.has_pef_pdf} title="PEF PDF Uploaded" onItemClick={makeBoolClick('has_pef_pdf', 'PEF PDF')} />
        </div>
      </SectionWrapper>

      {/* Section 8: Property Checklist */}
      <SectionWrapper id="checklist" title="Property Checklist" icon={Shield}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          {[
            ['master_form', 'Master Form'],
            ['noc_agreement', 'NOC Agreement'],
            ['lease_agreement', 'Lease Agreement'],
            ['electricity_bill', 'Electricity Bill'],
            ['pan_card', 'PAN Card'],
            ['aadhar_card', 'Aadhar Card'],
            ['gst_certificate', 'GST Certificate'],
            ['cancelled_cheque', 'Cancelled Cheque'],
            ['property_photos', 'Property Photos'],
            ['collection_status', 'Collection Status'],
          ].map(([key, label]) => (
            <MiniPie key={key} data={checklist[key] || []} title={label} onItemClick={makeChecklistClick(key, label)} />
          ))}
        </div>
      </SectionWrapper>

      {/* Section 9: Bank Details */}
      <SectionWrapper id="bank" title="Bank Details" icon={Banknote}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <MiniBar data={bank.bank_name.slice(0, 10)} title="Top Banks" onItemClick={makeClick('bank_name', 'Bank Name')} layout="vertical" height={200} activeCrossValues={crossFilters.bank_name || []} />
          <div className="space-y-4">
            <BooleanStat data={bank.has_account_number} title="Has Account Number" onItemClick={makeBoolClick('has_account_number', 'Account Number')} />
            <BooleanStat data={bank.has_ifsc} title="Has IFSC Code" onItemClick={makeBoolClick('has_ifsc', 'IFSC Code')} />
          </div>
          <BooleanStat data={bank.has_cheque_pdf} title="Has Cancelled Cheque PDF" onItemClick={makeBoolClick('has_cheque_pdf', 'Cheque PDF')} />
        </div>
      </SectionWrapper>

      {/* Section 10: OTA Information */}
      <SectionWrapper id="ota-info" title="OTA Information" icon={Globe2}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <MiniBar data={ota_info.ota_names} title="OTA Platforms" onItemClick={makeClick('ota_names', 'OTA Platform')} layout="vertical" height={220} activeCrossValues={crossFilters.ota_name || []} />
          <MiniPie data={ota_info.ota_statuses} title="OTA Status Distribution" onItemClick={makeClick('ota_statuses', 'OTA Status')} />
          <div className="space-y-4">
            <MiniPie data={ota_info.ota_locked.map(d => ({ ...d, name: d.name === 'true' || d.name === true ? 'Locked' : d.name === 'false' || d.name === false ? 'Unlocked' : d.name }))} title="OTA Lock Status" onItemClick={(item) => {
              const val = item.name === 'Locked' ? 'true' : 'false';
              openDrilldown('ota_locked', { ...item, name: val }, 'OTA Locked');
            }} />
            <MiniPie data={ota_info.ota_timer_running.map(d => ({ ...d, name: d.name === 'true' || d.name === true ? 'Running' : d.name === 'false' || d.name === false ? 'Stopped' : d.name }))} title="Timer Status" onItemClick={(item) => {
              const val = item.name === 'Running' ? 'true' : 'false';
              openDrilldown('ota_timer_running', { ...item, name: val }, 'Timer Running');
            }} />
          </div>
          <MiniPie data={ota_info.ota_access} title="Hotel Access Status" onItemClick={makeClick('ota_access', 'Hotel Access')} />
        </div>
      </SectionWrapper>
    </div>
  );
};

export default LeadAnalytics;
